/**
 * Authentication Service for MEV-Med System
 * Handles login, logout, token management, and user profile operations
 */

import axios from 'axios';
import { httpClient } from './httpClient';
import {
  LoginCredentials,
  LoginResponse,
  User,
  RefreshTokenResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  MfaRequiredResponse,
  MfaVerifyRequest,
  MfaStatus,
  TotpSetupResponse,
  MfaEnforcedRole,
} from '../types/auth';
import { ApiResponse } from '../types/common';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export class AuthService {
  // Authentication endpoints
  private readonly endpoints = {
    login: 'auth/login',
    logout: 'auth/logout',
    refresh: 'auth/refresh',
    me: 'auth/me',
    validate: 'auth/validate',
    changePassword: 'auth/change-password',
    forgotPassword: 'auth/forgot-password',
    resetPassword: 'auth/reset-password',
    health: 'auth/health',
    // MFA endpoints
    mfaVerify: 'auth/mfa/verify',
    mfaResendEmail: 'auth/mfa/resend-email',
    mfaStatus: 'auth/mfa/status',
    mfaTotpSetup: 'auth/mfa/totp/setup',
    mfaTotpConfirm: 'auth/mfa/totp/confirm',
    mfaEmailEnable: 'auth/mfa/email/enable',
    mfaMethod: 'auth/mfa/method',
    mfaDisable: 'auth/mfa/disable',
    mfaRecoveryRegenerate: 'auth/mfa/recovery-codes/regenerate',
    mfaEnforce: 'auth/mfa/enforce',
  };

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse | { message: string; data: MfaRequiredResponse }> {
    try {
      console.log('🔐 [AUTH] Starting login process...', { email: credentials.email });

      const response = await httpClient.post<LoginResponse['data'] | MfaRequiredResponse>(this.endpoints.login, {
        email: credentials.email,
        password: credentials.password,
      });

      if (response.data && 'mfaRequired' in response.data && response.data.mfaRequired) {
        // MFA required — don't store tokens yet
        console.log('[AUTH] MFA required for user');

        // Store remember me preference
        if (credentials.rememberMe) {
          localStorage.setItem('mev_remember_me', 'true');
          localStorage.setItem('mev_user_email', credentials.email);
        } else {
          localStorage.removeItem('mev_remember_me');
          localStorage.removeItem('mev_user_email');
        }

        return {
          message: response.message ?? 'Verificação MFA necessária',
          data: response.data as MfaRequiredResponse,
        };
      }

      if (response.data) {
        const data = response.data as LoginResponse['data'];
        // Store tokens
        this.storeTokens(data.accessToken, data.refreshToken);

        // Store remember me preference
        if (credentials.rememberMe) {
          localStorage.setItem('mev_remember_me', 'true');
          localStorage.setItem('mev_user_email', credentials.email);
        } else {
          localStorage.removeItem('mev_remember_me');
          localStorage.removeItem('mev_user_email');
        }

        console.log('✅ [AUTH] Login successful', { userId: data.user.id });

        return {
          message: response.message ?? 'Login realizado com sucesso',
          data,
        };
      }

      throw new Error('Resposta inválida do servidor');
    } catch (error) {
      console.error('❌ [AUTH] Login failed:', error);
      throw error;
    }
  }

  // ── MFA Methods ─────────────────────────────────────────────────────────

  async verifyMfa(data: MfaVerifyRequest): Promise<LoginResponse> {
    const response = await httpClient.post<LoginResponse['data']>(this.endpoints.mfaVerify, data);
    if (response.data) {
      this.storeTokens(response.data.accessToken, response.data.refreshToken);
      return { message: response.message ?? 'MFA verificado', data: response.data };
    }
    throw new Error('Resposta inválida do servidor');
  }

  async resendEmailOtp(challengeToken: string): Promise<void> {
    await httpClient.post(this.endpoints.mfaResendEmail, { mfaChallengeToken: challengeToken });
  }

  async getMfaStatus(): Promise<MfaStatus> {
    const response = await httpClient.get<MfaStatus>(this.endpoints.mfaStatus);
    if (response.data) return response.data;
    throw new Error('Erro ao buscar status MFA');
  }

  async initiateTotpSetup(): Promise<TotpSetupResponse> {
    const response = await httpClient.post<TotpSetupResponse>(this.endpoints.mfaTotpSetup);
    if (response.data) return response.data;
    throw new Error('Erro ao iniciar setup TOTP');
  }

  async confirmTotpSetup(code: string): Promise<{ recoveryCodes: string[] }> {
    const response = await httpClient.post<{ recoveryCodes: string[] }>(this.endpoints.mfaTotpConfirm, { code });
    if (response.data) return response.data;
    throw new Error('Erro ao confirmar TOTP');
  }

  async enableEmailMfa(): Promise<{ recoveryCodes: string[] }> {
    const response = await httpClient.post<{ recoveryCodes: string[] }>(this.endpoints.mfaEmailEnable);
    if (response.data) return response.data;
    throw new Error('Erro ao habilitar MFA por email');
  }

  async setMfaMethod(method: 'totp' | 'email'): Promise<void> {
    await httpClient.post(this.endpoints.mfaMethod, { method });
  }

  async disableMfa(): Promise<void> {
    await httpClient.post(this.endpoints.mfaDisable);
  }

  async regenerateRecoveryCodes(): Promise<{ recoveryCodes: string[] }> {
    const response = await httpClient.post<{ recoveryCodes: string[] }>(this.endpoints.mfaRecoveryRegenerate);
    if (response.data) return response.data;
    throw new Error('Erro ao regenerar códigos');
  }

  async enforceMfaForRole(roleId: number, enforce: boolean): Promise<void> {
    await httpClient.post(this.endpoints.mfaEnforce, { roleId, enforce });
  }

  async getEnforcedRoles(): Promise<MfaEnforcedRole[]> {
    const response = await httpClient.get<MfaEnforcedRole[]>(this.endpoints.mfaEnforce);
    if (response.data) return response.data;
    throw new Error('Erro ao buscar roles com enforcement');
  }

  /**
   * Logout user and clear stored data
   */
  async logout(): Promise<void> {
    try {
      console.log('🔓 [AUTH] Starting logout process...');
      
      // Call backend logout endpoint
      await httpClient.post(this.endpoints.logout);
      
      // Clear stored data
      this.clearStoredData();
      
      console.log('✅ [AUTH] Logout successful');
    } catch (error) {
      console.error('❌ [AUTH] Logout error:', error);
      // Clear stored data even if backend call fails
      this.clearStoredData();
    }
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<User> {
    try {
      console.log('👤 [AUTH] Fetching user profile...');
      
      const response = await httpClient.get<User>(this.endpoints.me);
      
      if (response.data) {
        console.log('✅ [AUTH] Profile loaded successfully', { userId: response.data.id });
        return response.data;
      }

      throw new Error('Perfil não encontrado');
    } catch (error) {
      console.error('❌ [AUTH] Failed to fetch profile:', error);
      throw error;
    }
  }

  /**
   * Refresh access token using refresh token.
   * Uses raw axios (not httpClient) to avoid interceptor loop.
   */
  async refreshToken(): Promise<string> {
    try {
      const refreshToken = this.getStoredRefreshToken();
      if (!refreshToken) {
        throw new Error('Refresh token not found');
      }

      console.log('[AUTH] Refreshing access token...');

      // Use raw axios to avoid httpClient interceptor loop
      const response = await axios.post(`${API_BASE_URL}/${this.endpoints.refresh}`, {
        refreshToken,
      });

      const data = response.data?.data as RefreshTokenResponse | undefined;

      if (data?.accessToken) {
        this.storeTokens(data.accessToken, data.refreshToken ?? refreshToken);
        console.log('[AUTH] Token refreshed successfully');
        return data.accessToken;
      }

      throw new Error('Failed to refresh token');
    } catch (error) {
      console.error('[AUTH] Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Validate current access token
   */
  async validateToken(): Promise<boolean> {
    try {
      const token = this.getStoredAccessToken();
      if (!token) {
        return false;
      }

      await httpClient.get(this.endpoints.validate);
      return true;
    } catch (error) {
      console.error('❌ [AUTH] Token validation failed:', error);
      return false;
    }
  }

  /**
   * Change user password
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    try {
      console.log('🔐 [AUTH] Changing password...');
      
      await httpClient.post(this.endpoints.changePassword, data);
      
      console.log('✅ [AUTH] Password changed successfully');
    } catch (error) {
      console.error('❌ [AUTH] Password change failed:', error);
      throw error;
    }
  }

  /**
   * Request password reset
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    try {
      console.log('📧 [AUTH] Requesting password reset...');
      
      await httpClient.post(this.endpoints.forgotPassword, data);
      
      console.log('✅ [AUTH] Password reset email sent');
    } catch (error) {
      console.error('❌ [AUTH] Password reset request failed:', error);
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    try {
      console.log('🔐 [AUTH] Resetting password...');
      
      await httpClient.post(this.endpoints.resetPassword, data);
      
      console.log('✅ [AUTH] Password reset successfully');
    } catch (error) {
      console.error('❌ [AUTH] Password reset failed:', error);
      throw error;
    }
  }

  /**
   * Check authentication service health
   */
  async checkHealth(): Promise<ApiResponse> {
    return httpClient.get(this.endpoints.health);
  }

  // Token management methods — read from Zustand persist key first, fallback to legacy keys
  getStoredAccessToken(): string | null {
    try {
      const stored = sessionStorage.getItem('mev-auth-store');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.state?.accessToken) return parsed.state.accessToken;
      }
    } catch { /* ignore */ }
    return sessionStorage.getItem('mev_access_token');
  }

  getStoredRefreshToken(): string | null {
    try {
      const stored = sessionStorage.getItem('mev-auth-store');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.state?.refreshToken) return parsed.state.refreshToken;
      }
    } catch { /* ignore */ }
    return sessionStorage.getItem('mev_refresh_token');
  }

  private storeTokens(accessToken: string, refreshToken: string): void {
    // Sync legacy keys (Zustand store syncs via setTokens in the store itself)
    sessionStorage.setItem('mev_access_token', accessToken);
    sessionStorage.setItem('mev_refresh_token', refreshToken);
  }

  clearStoredData(): void {
    sessionStorage.removeItem('mev_access_token');
    sessionStorage.removeItem('mev_refresh_token');
    sessionStorage.removeItem('mev_user_data');
  }

  // Check if user wants to be remembered
  getRememberMe(): boolean {
    return localStorage.getItem('mev_remember_me') === 'true';
  }

  getRememberedEmail(): string | null {
    return localStorage.getItem('mev_user_email');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getStoredAccessToken();
    return !!token;
  }

  // Decode JWT token (without verification - for client-side info only)
  decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  // Check if token is expired (client-side check)
  isTokenExpired(token?: string): boolean {
    const tokenToCheck = token || this.getStoredAccessToken();
    if (!tokenToCheck) return true;

    const decoded = this.decodeToken(tokenToCheck);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  }
}

// Create and export singleton instance
export const authService = new AuthService();
export default authService;